<template>
  <a-layout-header
    class="flex items-center justify-between !h-[72px] sm:!h-[48px] w-full !py-0 !px-1 sm:!px-4"
    :style="{ background: 'white', position: 'fixed', zIndex: 1000 }"
  >
    <div class="pl-0 sm:pl-1 flex items-center w-full">
      <span
        class="text-base font-bold cursor-pointer inline-block w-[calc(100vw-6rem)] md:w-[calc(280px-0.625rem)] mr-2"
      >
        <div @click="$router.push('/')" class="flex items-center cursor-pointer">
          <Logo />
          <span class="text-2xl font-bold ml-2"></span>
        </div>
        <MenuUnfoldOutlined
          v-show="props.showSiderToggle && layoutStore.siderCollapsed"
          @click="onChangeCollapsed"
        />
        <MenuFoldOutlined
          v-show="props.showSiderToggle && !layoutStore.siderCollapsed"
          @click="onChangeCollapsed"
        />
      </span>

      <div class="flex-1 flex justify-between items-center">
        <a-space class="hidden md:flex">
          <a-button type="primary" :class="{ 'bg-red': isAdmin }" class="pointer-events-none">
            {{ roleText }}
          </a-button>
          <a-select
            v-if="!isDischarger"
            :disabled="isCompanySelectionDisabled"
            v-model:value="selectedCompanyId"
            class="w-[100px] md:w-[200px] lg:w-[300px]"
            @change="handleCompanyChange"
          >
            <a-select-option
              v-for="company in trueCompanyList"
              :key="company.value"
              :value="company.value"
              >{{ company.label }}</a-select-option
            >
          </a-select>
        </a-space>

        <div class="flex items-center ml-2">
          <a-space class="mr-5" v-if="isDevMode">
            <a-select v-model:value="selectedLanguage" @change="updateLanguage">
              <a-select-option value="ja">日本語</a-select-option>
              <a-select-option value="en">English</a-select-option>
            </a-select>
          </a-space>

          <a-popover
            :arrow="false"
            trigger="click"
            placement="bottomRight"
            overlayClassName="w-[256px] menu-popover"
            :overlayInnerStyle="{ width: '256px' }"
            :getPopupContainer="(a: any) => a.parentNode"
            v-model:open="popoverVisible"
            class="mr-0 md:mr-10"
          >
            <template #content>
              <div>
                <a-row
                  v-if="isDischarger"
                  class="cursor-pointer mb-3"
                  @click="
                    $router.push('/account');
                    popoverVisible = false;
                  "
                >
                  <span class="ml-1"> {{ $t('account_information_update') }} </span>
                </a-row>

                <a-row
                  v-if="isTransporter"
                  class="cursor-pointer mb-3"
                  @click="
                    $router.push('/account/trans');
                    popoverVisible = false;
                  "
                >
                  <span class="ml-1"> {{ $t('account_information_update') }} </span>
                </a-row>
                <a-row
                  class="cursor-pointer mb-3"
                  @click="
                    $router.push('/change-password');
                    popoverVisible = false;
                  "
                >
                  <span class="ml-1"> {{ $t('change_password') }} </span>
                </a-row>

                <div v-if="isTransporter">
                  <a-row
                    v-if="isTransporter"
                    class="cursor-pointer mb-3"
                    @click="
                      goToCollectionAreaPriceSetting();
                      popoverVisible = false;
                    "
                  >
                    <span class="ml-1"> {{ $t('Collection area price setting') }} </span>
                  </a-row>
                </div>

                <a-row class="cursor-pointer" @click="handleLogout">
                  <span class="ml-1"> {{ $t('log_out') }} </span>
                </a-row>
              </div>
            </template>
            <span class="cursor-pointer flex items-center">
              <a-avatar :size="32" :class="{ '!bg-[#4294FE]': !isAdmin, 'bg-red': isAdmin }">
                <template #icon><UserOutlined /></template>
              </a-avatar>
              <span class="ml-2 max-w-[0px] md:max-w-[200px] truncate">{{ username }}</span>
            </span>
          </a-popover>

          <a-dropdown
            overlayClassName="cust-menu"
            :trigger="['click']"
            destroyPopupOnHide
            v-model:open="dropdownVisible"
          >
            <template #overlay>
              <Sider :isRight="true" />
            </template>

            <MenuOutlined class="ml-2 text-xl" />
          </a-dropdown>
        </div>
      </div>
    </div>
  </a-layout-header>
</template>

<script setup lang="ts">
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined
} from '@ant-design/icons-vue';
import { useLayoutStore } from '@/stores';
import Logo from '@/components/Logo.vue';
import Breadcrumb from './Breadcrumb.vue';
import Sider from './Sider.vue';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { router } from '@/router';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import { useAuthStore, useCommonStore, useUserStore, useAchievementStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useCrequestStore } from '@/stores/modules/crequest';
const route = useRoute();

let adminRoutes = [
  '/inquiry-admin',
  '/account-master',
  '/waste-master',
  '/unit-of-measure-master',
  '/packaging-master',
  '/disposal-site-master',
  '/transport-and-disposal-operator-master'
];

let isCompanySelectionDisabled = computed(() => {
  let currentRoute = route.path;

  let isDisabled = adminRoutes.some((adminRoute) => currentRoute.startsWith(adminRoute));
  if (isDisabled) {
    selectedCompanyId.value = 'ALL';
    companyId.value = -1;
  }
  return isDisabled;
});

const authStore = useAuthStore();
const achievementStore = useAchievementStore();
const cRequestStore = useCrequestStore();
const { isAdmin, isDischarger, isTransporter, username, isDefaultTransporter } =
  storeToRefs(authStore);
const selectedLanguage = ref(localStorage.getItem('language') ?? 'ja');

const isDevMode = ref(import.meta.env.MODE === 'dev');

const updateLanguage = (value: any) => {
  localStorage.setItem('language', value);
  window.location.reload();
};

const roleText = computed(() => {
  if (isAdmin.value) return t('Administrator');
  if (isDischarger.value) return t('discharge_business_operator');
  if (isTransporter.value) return t('transportation_and_disposal_contractor');
  return '';
});
const userStore = useUserStore();

const commonStore = useCommonStore();
const { companyList, selectedCompanyId, companyId } = storeToRefs(commonStore);

if (isTransporter.value) {
  commonStore.getWasteCompaniesWhoseProjectsAreManagedByTransportCompany();
}

const trueCompanyList = computed(() => {
  return [{ value: 'ALL', label: t('ALL') }].concat(companyList.value);
});

const props = defineProps<{
  showSiderToggle: boolean;
}>();

const layoutStore = useLayoutStore();

const onChangeCollapsed = () => {
  layoutStore.$patch({ siderCollapsed: !layoutStore.siderCollapsed });
};

const handleCompanyChange = (value: string) => {
  if (value !== 'ALL') {
    commonStore.setCompanyId(parseInt(value));
  } else {
    commonStore.setCompanyId(-1);
  }
};

const handleLogout = () => {
  localStorage.clear();
  authStore.clearCurrentUser();
  router.push('/login');
  localStorage.setItem('reload', String(Date.now()));
  commonStore.selectedCompanyId = 'ALL';
  commonStore.companyId = -1;
  cRequestStore.filters = [];
  achievementStore.filter = undefined;
};
const goToCollectionArea = () => {
  router.push('/collection-area');
};

const goToCollectionAreaPriceSetting = () => {
  router.push('/area-price-setting');
};

const popoverVisible = ref(false);
const dropdownVisible = ref(false);

// Update the handleResize function to handle both popover and dropdown
const handleResize = () => {
  if (popoverVisible.value) {
    popoverVisible.value = false;
    setTimeout(() => {
      popoverVisible.value = true;
    }, 0);
  }
  if (dropdownVisible.value) {
    dropdownVisible.value = false;
    setTimeout(() => {
      dropdownVisible.value = true;
    }, 0);
  }
};

// Add mounted and unmounted hooks
onMounted(() => {
  userStore.getUserInfo();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss">
.cust-menu .ant-dropdown-menu-item,
.ant-dropdown-menu-submenu-title {
  margin: 5px;
}
.cust-menu {
  z-index: 10000 !important;
}
.cust-menu .ant-dropdown .ant-dropdown-menu {
  background-color: #f8f8f8;
}
.menu-popover.ant-popover .ant-popover-inner {
  border-radius: 0;
}
</style>
